<template>
  <q-page class="av-page">
    <!-- Header -->
    <div class="av-header row items-center no-wrap q-px-lg q-py-sm">
      <q-icon
        name="precision_manufacturing"
        size="22px"
        color="primary"
        class="q-mr-sm"
      />
      <span class="text-subtitle1 text-weight-medium">Agente de Vendas</span>
      <q-space />
      <q-btn
        flat
        round
        dense
        icon="add_comment"
        color="grey-6"
        size="sm"
        @click="novaConversa"
      >
        <q-tooltip>Nova conversa</q-tooltip>
      </q-btn>
    </div>
    <q-separator />

    <!-- Área de mensagens -->
    <div class="av-messages" ref="mensagensRef">
      <!-- Empty state -->
      <div
        v-if="mensagens.length === 0 && !carregandoAgente"
        class="av-empty text-center"
      >
        <q-icon
          name="precision_manufacturing"
          size="64px"
          class="text-grey-3 q-mb-md"
        />
        <p class="text-h6 text-grey-5 q-mb-xs">Como posso ajudar?</p>
        <p class="text-body2 text-grey-4">
          Descreva o que você precisa ou adicione o consumo de EPIs do
          cliente<br />
          usando o botão <strong>+</strong> abaixo.
        </p>
      </div>

      <!-- Mensagens -->
      <div
        v-for="(msg, i) in mensagens"
        :key="i"
        :class="[
          'av-msg-wrap',
          msg.tipo === 'usuario' ? 'av-msg-wrap--user' : '',
        ]"
      >
        <q-avatar
          v-if="msg.tipo === 'agente'"
          size="30px"
          color="primary"
          text-color="white"
          class="av-avatar"
        >
          <q-icon name="smart_toy" size="16px" />
        </q-avatar>

        <div
          class="av-bubble"
          :class="
            msg.tipo === 'usuario' ? 'av-bubble--user' : 'av-bubble--agent'
          "
        >
          <div v-if="msg.tipo === 'agente' && msg.carregando">
            <div class="row items-center q-gutter-xs">
              <q-spinner-dots color="primary" size="16px" />
              <span class="text-caption text-grey-6">{{ statusAgente }}</span>
            </div>
            <div
              v-if="msg.pensamentos && msg.pensamentos.length > 0"
              class="av-thinking-preview q-mt-sm"
            >
              {{ msg.pensamentos[msg.pensamentos.length - 1] }}
            </div>
            <div v-else-if="!msg.html" class="q-mt-sm">
              <q-skeleton type="text" class="q-mb-xs" />
              <q-skeleton type="text" width="70%" />
            </div>
          </div>
          <div
            v-if="msg.tipo === 'agente' && msg.erro"
            class="av-erro-msg row items-start q-gutter-xs"
          >
            <q-icon
              name="error_outline"
              color="negative"
              size="16px"
              class="q-mt-xs"
            />
            <span>{{ msg.erro }}</span>
          </div>
          <details
            v-if="
              msg.tipo === 'agente' &&
              !msg.carregando &&
              msg.pensamentos &&
              msg.pensamentos.length > 0
            "
            class="av-thinking-details q-mb-sm"
          >
            <summary class="text-caption text-grey-5">
              Ver raciocínio ({{ msg.pensamentos.length }} etapa(s))
            </summary>
            <div
              v-for="(p, pi) in msg.pensamentos"
              :key="pi"
              class="av-thinking-step"
            >
              {{ p }}
            </div>
          </details>
          <div
            v-if="msg.tipo === 'agente' && msg.semResposta"
            class="av-sem-resposta"
          >
            O agente processou mas não gerou uma resposta. Peça para continuar
            ou reformule a pergunta.
          </div>
          <span v-if="msg.tipo === 'usuario'" class="av-user-text">{{
            msg.texto
          }}</span>
          <div
            v-if="msg.tipo === 'agente' && msg.html"
            class="agente-resposta-markdown"
            v-html="msg.html"
          />
        </div>

        <q-avatar
          v-if="msg.tipo === 'usuario'"
          size="30px"
          color="grey-3"
          text-color="grey-7"
          class="av-avatar"
        >
          <q-icon name="person" size="16px" />
        </q-avatar>
      </div>
    </div>

    <!-- Chip de contexto ativo -->
    <transition name="av-context-fade">
      <div
        v-if="produtosSelecionados.length > 0"
        class="av-context-bar q-px-lg q-py-xs"
      >
        <q-chip
          icon="inventory_2"
          color="primary"
          text-color="white"
          removable
          dense
          clickable
          class="q-ma-none"
          @click="abrirModal"
          @remove="produtosSelecionados = []"
        >
          {{ produtosSelecionados.length }} produto(s) como contexto
        </q-chip>
      </div>
    </transition>

    <!-- Input -->
    <div class="av-input-area q-px-lg q-pb-md q-pt-xs">
      <div class="av-input-box">
        <q-btn
          flat
          round
          dense
          icon="add"
          color="grey-6"
          size="md"
          class="q-mr-xs flex-shrink-0"
          @click="abrirModal"
        >
          <q-tooltip>Adicionar contexto de EPIs</q-tooltip>
        </q-btn>
        <q-input
          v-model="inputTexto"
          borderless
          placeholder="Descreva o que você precisa..."
          class="col av-input-field"
          autogrow
          :maxlength="2000"
          @keydown.enter.exact.prevent="enviar"
        />
        <q-btn
          flat
          round
          dense
          icon="send"
          color="primary"
          size="md"
          class="flex-shrink-0"
          :disable="!inputTexto.trim() || carregandoAgente"
          :loading="carregandoAgente"
          @click="enviar"
        />
      </div>
      <div class="text-caption text-grey-4 text-center q-mt-xs">
        Enter para enviar · Shift+Enter para nova linha
      </div>
    </div>

    <!-- Modal de seleção de EPIs -->
    <q-dialog v-model="modalAberto" persistent>
      <q-card class="av-modal">
        <q-card-section class="row items-center q-pb-none">
          <q-icon
            name="inventory_2"
            color="primary"
            class="q-mr-sm"
            size="sm"
          />
          <span class="text-subtitle1 text-weight-medium"
            >Contexto — Consumo de EPIs</span
          >
          <q-space />
          <q-btn flat round dense icon="close" v-close-popup />
        </q-card-section>
        <q-card-section class="q-pt-xs">
          <p class="text-body2 text-grey-6 q-mb-none">
            Selecione os produtos que o cliente consome e informe quantidade e
            periodicidade.
          </p>
        </q-card-section>
        <q-separator />
        <q-card-section class="av-modal-body">
          <SelecionarMaquina v-model="produtosSelecionadosTemp" />
        </q-card-section>
        <q-separator />
        <q-card-actions align="right" class="q-pa-md">
          <q-btn flat label="Cancelar" color="grey-7" v-close-popup />
          <q-btn
            unelevated
            label="Confirmar contexto"
            color="primary"
            icon="check"
            :disable="produtosSelecionadosTemp.length === 0"
            @click="confirmarContexto"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script>
import { ref, nextTick } from "vue";
import { useStore } from "vuex";
import { marked } from "marked";
import SelecionarMaquina from "../components/SelecionarMaquina.vue";

const AGENT_URL =
  process.env.VUE_APP_AGENTE_VENDAS_URL ||
  "https://mtpx-sales-agent-ve6fopngaa-uc.a.run.app";
// "http://localhost:8080";

export default {
  name: "AgenteVendas",
  components: { SelecionarMaquina },
  setup() {
    const store = useStore();

    const mensagensRef = ref(null);
    const inputTexto = ref("");
    const mensagens = ref([]);
    const carregandoAgente = ref(false);
    const statusAgente = ref("");
    const sessionId = ref(null);

    const produtosSelecionados = ref([]);
    const produtosSelecionadosTemp = ref([]);
    const modalAberto = ref(false);

    function getUserId() {
      return store.state?.autenticacao?.usuarioLogado?.email || "anonimo";
    }

    async function scrollToBottom() {
      await nextTick();
      if (mensagensRef.value) {
        mensagensRef.value.scrollTop = mensagensRef.value.scrollHeight;
      }
    }

    function abrirModal() {
      produtosSelecionadosTemp.value = produtosSelecionados.value.map((p) => ({
        ...p,
      }));
      modalAberto.value = true;
    }

    function confirmarContexto() {
      produtosSelecionados.value = [...produtosSelecionadosTemp.value];
      modalAberto.value = false;
    }

    function novaConversa() {
      mensagens.value = [];
      sessionId.value = null;
      produtosSelecionados.value = [];
      inputTexto.value = "";
    }

    async function chamarAgente({ items = [], message = "" }) {
      carregandoAgente.value = true;
      statusAgente.value = "Iniciando...";

      const msgId = Date.now();
      mensagens.value.push({
        id: msgId,
        tipo: "agente",
        html: "",
        carregando: true,
        pensamentos: [],
      });
      await scrollToBottom();

      // Acessa a mensagem sempre via array para respeitar o Proxy do Vue
      const getMsgAgente = () => mensagens.value.find((m) => m.id === msgId);

      const body = { userId: getUserId() };
      if (sessionId.value) body.sessionId = sessionId.value;
      if (message) body.message = message;
      if (items.length > 0) body.items = items;

      try {
        const res = await fetch(`${AGENT_URL}/chat/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let currentEvent = null;
        let rawText = "";

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith("data: ") && currentEvent) {
              let payload;
              try {
                payload = JSON.parse(line.slice(6));
              } catch {
                currentEvent = null;
                continue;
              }

              switch (currentEvent) {
                case "thinking": {
                  const m = getMsgAgente();
                  if (m && payload.text) m.pensamentos.push(payload.text);
                  statusAgente.value = "Analisando...";
                  break;
                }
                case "tool_call":
                  console.log("[SSE] tool_call", payload);
                  statusAgente.value = "Consultando base de dados...";
                  break;
                case "tool_result":
                  console.log("[SSE] tool_result OK");
                  statusAgente.value = "Processando resultados...";
                  break;
                case "text": {
                  console.log("[SSE] text chunk:", payload);
                  rawText += payload.text || "";
                  const m = getMsgAgente();
                  if (m) m.html = marked.parse(rawText);
                  statusAgente.value = "";
                  scrollToBottom();
                  break;
                }
                case "done": {
                  console.log(
                    "[SSE] done — rawText length:",
                    rawText.length,
                    payload
                  );
                  sessionId.value = payload.sessionId || sessionId.value;
                  const m = getMsgAgente();
                  if (m) {
                    m.carregando = false;
                    if (!rawText) {
                      m.semResposta = true;
                    }
                  }
                  carregandoAgente.value = false;
                  statusAgente.value = "";
                  break;
                }
                case "error":
                  console.warn("[SSE] error event:", payload);
                  throw new Error(payload.error || "Erro interno do agente");
              }
              currentEvent = null;
            }
          }
        }
      } catch (err) {
        const m = getMsgAgente();
        if (m) {
          m.carregando = false;
          m.erro = err?.message || "Erro ao comunicar com o agente.";
        }
      } finally {
        carregandoAgente.value = false;
        statusAgente.value = "";
        // Apenas garante que o spinner pare — nunca remove mensagem com conteúdo
        const residual = getMsgAgente();
        if (residual && residual.carregando) {
          residual.carregando = false;
          if (!residual.html && !residual.pensamentos.length) {
            mensagens.value = mensagens.value.filter((m) => m.id !== msgId);
          }
        }
        await scrollToBottom();
      }
    }

    async function enviar() {
      const texto = inputTexto.value.trim();
      if (!texto || carregandoAgente.value) return;

      inputTexto.value = "";

      const items = produtosSelecionados.value.map((p) => {
        const item = {
          nome_produto: p.nome_produto,
          quantidade_consumo: p.quantidade,
          periodicidade: p.periodicidade,
        };
        if (p.ca_modelo) item.ca_modelo = p.ca_modelo;
        return item;
      });

      const textoExibicao =
        items.length > 0
          ? `${texto}\n\n(contexto: ${items.length} produto(s) selecionado(s))`
          : texto;

      mensagens.value.push({ tipo: "usuario", texto: textoExibicao });
      await scrollToBottom();

      await chamarAgente({ items, message: texto });
    }

    return {
      mensagensRef,
      inputTexto,
      mensagens,
      carregandoAgente,
      statusAgente,
      produtosSelecionados,
      produtosSelecionadosTemp,
      modalAberto,
      abrirModal,
      confirmarContexto,
      novaConversa,
      enviar,
    };
  },
};
</script>

<style scoped>
.av-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f9f9f9;
}

.av-header {
  background: #fff;
  flex-shrink: 0;
  min-height: 52px;
}

.av-messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  align-self: center;
}

.av-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 16px;
}

.av-msg-wrap {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.av-msg-wrap--user {
  flex-direction: row-reverse;
}

.av-avatar {
  flex-shrink: 0;
  margin-top: 2px;
}

.av-bubble {
  max-width: 85%;
  padding: 12px 16px;
  border-radius: 14px;
  line-height: 1.6;
  font-size: 0.92rem;
}

.av-bubble--user {
  background: var(--q-primary);
  color: #fff;
  border-top-right-radius: 4px;
}

.av-bubble--agent {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-top-left-radius: 4px;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.av-user-text {
  white-space: pre-wrap;
}

.av-sem-resposta {
  font-size: 0.82rem;
  color: #999;
  font-style: italic;
  padding: 4px 0;
}

.av-erro-msg {
  font-size: 0.82rem;
  color: #c62828;
  background: #fff3f3;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 4px;
}

.av-thinking-preview {
  font-size: 0.78rem;
  color: #aaa;
  font-style: italic;
  line-height: 1.5;
  border-left: 2px solid #e8e8e8;
  padding-left: 8px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 120px;
  overflow-y: auto;
}

.av-thinking-details {
  border-left: 2px solid #f0f0f0;
  padding-left: 10px;
  margin-bottom: 4px;
}

.av-thinking-details summary {
  cursor: pointer;
  outline: none;
  list-style: none;
  color: #bbb;
  font-size: 0.75rem;
}

.av-thinking-details summary::-webkit-details-marker {
  display: none;
}

.av-thinking-details[open] summary {
  color: #999;
  margin-bottom: 4px;
}

.av-thinking-step {
  font-size: 0.78rem;
  color: #aaa;
  font-style: italic;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 6px 0;
  border-top: 1px dashed #f0f0f0;
}

.av-context-bar {
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  align-self: center;
}

.av-context-fade-enter-active,
.av-context-fade-leave-active {
  transition: all 0.2s ease;
}
.av-context-fade-enter-from,
.av-context-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

.av-input-area {
  background: #f9f9f9;
  flex-shrink: 0;
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  align-self: center;
}

.av-input-box {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 14px;
  padding: 6px 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: border-color 0.2s;
}

.av-input-box:focus-within {
  border-color: var(--q-primary);
}

.av-modal {
  width: 760px;
  max-width: 95vw;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}

.av-modal-body {
  flex: 1;
  overflow-y: auto;
}

.agente-resposta-markdown {
  line-height: 1.7;
}
.agente-resposta-markdown :deep(h2) {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 1.2em 0 0.4em;
  color: #333;
}
.agente-resposta-markdown :deep(h3) {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 1em 0 0.3em;
}
.agente-resposta-markdown :deep(p) {
  margin: 0.4em 0;
}
.agente-resposta-markdown :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0.8em 0;
  font-size: 0.85rem;
}
.agente-resposta-markdown :deep(th) {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  padding: 7px 10px;
  text-align: left;
  font-weight: 600;
}
.agente-resposta-markdown :deep(td) {
  border: 1px solid #e0e0e0;
  padding: 5px 10px;
}
.agente-resposta-markdown :deep(tr:nth-child(even)) {
  background-color: #fafafa;
}
.agente-resposta-markdown :deep(ul),
.agente-resposta-markdown :deep(ol) {
  padding-left: 1.4em;
  margin: 0.4em 0;
}
.agente-resposta-markdown :deep(strong) {
  font-weight: 600;
}
</style>
